import { useUserInfo } from "@/api/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/utils";
import React from "react";

function getInitials(name: string | undefined): string {
  if (!name) return "?";
  return (
    name
      .split(/\s+/)
      .map((segment) => segment[0])
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

/** Compact rail-footer trigger: avatar, name, organization. Must forward ref for PopoverTrigger asChild. */
export const AccountPopoverRailFooterTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button">
>(function AccountPopoverRailFooterTrigger({ className, ...props }, ref) {
  const { data: userInfo } = useUserInfo();
  const user = userInfo?.user;
  const orgName = userInfo?.organization?.name ?? "Organization";

  return (
    <button
      ref={ref}
      type="button"
      title={user?.email ?? user?.name}
      className={cn(
        "flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left outline-none transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
        className,
      )}
      {...props}
    >
      <Avatar size="default" tint="rust">
        {user?.picture_url && (
          <AvatarImage src={user.picture_url} alt={user.name ?? ""} />
        )}
        <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
      </Avatar>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-n-90">
          {user?.name ?? "Account"}
        </span>
        <span className="block truncate font-mono text-2xs text-n-50">
          {orgName}
        </span>
      </span>
    </button>
  );
});
